import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Hook for invalidating all order-related queries.
 * 
 * DESIGN DECISION: Broad invalidation is intentional.
 * 
 * Order mutations can affect multiple views simultaneously:
 * - Orders list (pagination, filters)
 * - Today's board (status-based grouping)
 * - Dashboard KPIs (counts, revenue)
 * - Delivery stats (courier assignments)
 * - Group counts (sidebar badges)
 * 
 * Targeted invalidation via setQueryData was considered but rejected because:
 * 1. Adds significant complexity mapping mutations to affected queries
 * 2. Easy to miss edge cases (e.g., filter changes, date boundaries)
 * 3. No measurable performance benefit at current scale (~500 orders)
 * 
 * SCALE THRESHOLD: If mutation frequency exceeds 10/second or total
 * queries become expensive (>100ms combined), consider targeted updates.
 */
export const useInvalidateOrderQueries = () => {
  const queryClient = useQueryClient();

  const invalidateAll = useCallback(() => {
    // Invalidate all orders-related queries for instant updates
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['today-orders'] });
    queryClient.invalidateQueries({ queryKey: ['today-kpis'] });
    queryClient.invalidateQueries({ queryKey: ['orderGroupCounts'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
    queryClient.invalidateQueries({ queryKey: ['delivery-stats'] });
  }, [queryClient]);

  return { invalidateAll };
};
